<template>
  <div class="dictionary-list">
    <el-row :gutter="20" class="operate-bar">
      <el-col :span="8" align="left">
        <!-- 新增字典类型按钮 -->
        <el-button icon="el-icon-plus" type="primary" @click="openAddDialog"
          v-hasPermission="'dict:edit'">新增字典类型</el-button>
        <!-- 批量删除按钮（选中字典类型后可用） -->
        <el-button icon="el-icon-delete" type="danger" v-hasPermission="'dict:edit'" @click="handleBatchDelete"
          :disabled="!this.hasPermission('dict:edit') || selectedIds.length === 0">批量删除</el-button>
      </el-col>
      <!-- 搜索区域 -->
      <el-col :span="16" align="right">
        <el-form :inline="true" :model="searchForm" class="search-form">
          <el-form-item label="字典类型编码">
            <el-input v-model="searchForm.dictTypeCode" placeholder="请输入字典类型编码" maxlength="20"></el-input>
          </el-form-item>
          <el-form-item label="字典类型名称">
            <el-input v-model="searchForm.dictTypeName" placeholder="请输入字典类型名称" maxlength="20"></el-input>
          </el-form-item>
          <el-button icon="el-icon-search" type="primary" @click="loadDictionaries">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-table :data="dictionaries" border stripe style="width: 100%" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="55" align="center"></el-table-column>
      <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
      <el-table-column prop="dictTypeCode" label="字典类型编码" header-align="center"></el-table-column>
      <el-table-column prop="dictTypeName" label="字典类型名称" header-align="center"></el-table-column>
      <el-table-column prop="description" label="描述" header-align="center"></el-table-column>
      <el-table-column prop="createTime" label="创建时间"
        :formatter="(row, column, cellValue) => $options.filters.formatTime(cellValue)"
        align="center"></el-table-column>
      <el-table-column label="操作" align="center">
        <template slot-scope="scope">
          <el-tooltip content="编辑" placement="top">
            <el-button icon="el-icon-edit" type="primary" circle v-hasPermission="'dict:edit'"
              @click="openEditDialog(scope.row)"></el-button>
          </el-tooltip>
          <el-tooltip content="查看字典项" placement="top">
            <el-button icon="el-icon-coin" type="warning" circle v-hasPermission="'dict:edit'"
              @click="$router.push({ name: 'DictionaryItemList', query: { dictTypeId: scope.row.id } })"></el-button>
          </el-tooltip>
          <el-tooltip content="删除" placement="top">
            <el-button icon="el-icon-delete" type="danger" circle v-hasPermission="'dict:edit'"
              @click="handleDelete(scope.row.id)"></el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="currentPage"
      :page-sizes="[10, 20, 50]" :page-size="pageSize" layout="total, sizes, prev, pager, next, jumper"
      :total="total"></el-pagination>

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="form" :rules="rules" ref="form" label-width="150px">
        <el-form-item label="字典类型编码" prop="dictTypeCode">
          <el-input v-model="form.dictTypeCode" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="字典类型名称" prop="dictTypeName">
          <el-input v-model="form.dictTypeName" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input type="textarea" v-model="form.description" rows="3" maxlength="200"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false; $refs.form.resetFields()">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import dictApi from '../../api/dictionary'
import { hasPermission } from '../../utils/hasPermission'

export default {
  name: 'DictionaryList',
  data() {
    // 字典类型编码唯一校验
    const checkTypeCodeUnique = async (rule, value, callback) => {
      if (!value) {
        // 空值不校验（由required规则处理）
        return callback()
      }

      try {
        const params = {
          dictTypeCode: value,
          excludeId: this.isEdit ? this.recordId : null
        }
        // 调用后端查重接口
        const response = await dictApi.checkTypeCode(params)
        if (response.success && response.exists) {
          // 字典类型编码已存在，返回错误提示
          callback(new Error('该字典类型编码已被使用，请更换'))
        } else {
          // 字典类型编码可用，验证通过
          callback()
        }
      } catch (error) {
        callback(new Error('检查失败，请稍后重试'))
      }
    }

    return {
      // 表格数据
      dictionaries: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,

      // 搜索表单
      searchForm: {
        dictTypeCode: '',
        dictTypeName: ''
      },

      // 选中的行
      selectedIds: [],

      // 对话框
      dialogVisible: false,
      dialogTitle: '新增字典类型',
      isEdit: false,
      recordId: null, // 编辑时记录当前字典类型的ID，用于排除自身
      form: {
        id: null,
        dictTypeCode: '',
        dictTypeName: '',
        description: '',
        status: 1
      },
      rules: {
        dictTypeCode: [
          { required: true, message: '请输入字典类型编码', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9]+$/, message: '字典类型编码只能包含字母和数字', trigger: 'blur' },
          { min: 2, max: 20, message: '字典类型编码长度在 2 到 20 个字符', trigger: 'blur' },
          { validator: checkTypeCodeUnique, trigger: 'blur' }
        ],
        dictTypeName: [
          { required: true, message: '请输入字典类型名称', trigger: 'blur' },
          { min: 1, max: 20, message: '字典类型名称长度在 1 到 20 个字符', trigger: 'blur' }
        ],
        description: [
          { min: 1, max: 200, message: '描述长度在 1 到 200 个字符', trigger: 'blur' }
        ]
      }
    }
  },

  created() {
    this.loadDictionaries()
  },

  methods: {
    // 加载字典类型列表
    loadDictionaries() {
      // 组装请求参数
      const params = {
        dictTypeCode: this.searchForm.dictTypeCode, // 搜索-字典类型编码
        dictTypeName: this.searchForm.dictTypeName, // 搜索-字典类型名称
        page: this.currentPage,                     // 分页-当前页
        size: this.pageSize                         // 分页-每页条数
      };
      dictApi.findAllDictType(params).then(response => {
        if (response.success) {
          this.dictionaries = response.data.data
          this.total = response.data.total
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载字典类型失败：' + error.message)
      })
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        dictTypeCode: '',
        dictTypeCode: ''
      }
      this.loadDictionaries()
    },

    // 每页条数变化时
    handleSizeChange(val) {
      this.pageSize = val;
      this.loadDictionaries(); // 重新加载
    },

    // 当前页变化时
    handleCurrentChange(val) {
      this.currentPage = val;
      this.loadDictionaries(); // 重新加载
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id)
    },

    // 打开新增对话框
    openAddDialog() {
      if (!hasPermission('dict:edit')) {
        this.$message.error('无新增字典类型权限');
        return;
      }
      this.dialogTitle = '新增字典类型'
      this.isEdit = false
      this.recordId = null
      this.form = {
        id: null,
        dictTypeCode: '',
        dictTypeName: '',
        description: '',
        status: 1
      }
      this.dialogVisible = true
    },

    // 打开编辑对话框
    openEditDialog(row) {
      if (!hasPermission('dict:edit')) {
        this.$message.error('无编辑字典类型权限');
        return;
      }
      this.dialogTitle = '编辑字典类型'
      this.isEdit = true
      this.recordId = row.id
      this.form = { ...row }
      this.dialogVisible = true
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          if (this.isEdit) {
            // 更新字典类型
            dictApi.updateDictType(this.form.id, this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '更新成功')
                this.dialogVisible = false
                this.loadDictionaries()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('更新失败：' + error.message)
            })
          } else {
            // 新增字典类型
            dictApi.addDictType(this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '新增成功')
                this.dialogVisible = false
                this.loadDictionaries()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('新增失败：' + error.message)
            })
          }
        }
      })
    },

    // 删除字典类型
    handleDelete(id) {
      if (!hasPermission('dict:edit')) {
        this.$message.error('无删除字典类型权限');
        return;
      }
      this.$confirm('确定要删除该字典类型及字典项吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        dictApi.deleteDictType(id).then(response => {
          if (response.success) {
            this.$message.success(response.message || '删除成功')
            this.loadDictionaries()
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    // 批量删除字典类型
    handleBatchDelete() {
      if (!hasPermission('dict:edit')) {
        this.$message.error('无批量删除字典类型权限');
        return;
      }
      if (this.selectedIds.length === 0) {
        this.$message.warning('请选择要删除的字典类型');
        return;
      }
      this.$confirm('确定要批量删除选中的字典类型及字典项吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        dictApi.deleteDictTypeBatch(this.selectedIds).then(response => {
          if (response.success) {
            this.$message.success(response.message || '批量删除成功')
            this.loadDictionaries()
            this.selectedIds = []
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('批量删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    hasPermission(permCode) {
      return hasPermission(permCode);
    },
  }
}
</script>

<style scoped></style>